import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import {
  LIFT_KEYS, REFERENCE_TYPES, INTENSITY_MODES,
} from "@/lib/strength";
import {
  DISCIPLINE_IDS, getDiscipline, resolveExerciseModality,
  formatExercisePrescription, computeExerciseTarget, ZONE_OPTIONS,
} from "@/lib/trainingDisciplines";

const HYBRID_MODALITIES = DISCIPLINE_IDS.filter((id) => id !== "hybrid");

function ModalitySelect({ value, onChange }) {
  return (
    <Select value={value || "strength"} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-[10px] w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {HYBRID_MODALITIES.map((id) => {
          const d = getDiscipline(id);
          const Icon = d.icon;
          return (
            <SelectItem key={id} value={id}>
              <span className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" />
                {d.shortLabel}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function StrengthRow({ ex, onChange, onRemove, ctx }) {
  const target = computeExerciseTarget(ex, "strength", ctx);
  const line = formatExercisePrescription(ex, "strength", ctx);

  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 sm:grid-cols-[1fr,64px,48px,56px,40px,56px,48px,48px,32px] gap-1.5">
        <Input value={ex.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Exercice" className="h-9 text-sm col-span-2 sm:col-span-1" />
        <Select value={ex.lift_key || "none"} onValueChange={(v) => onChange("lift_key", v === "none" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Lift" /></SelectTrigger>
          <SelectContent>{LIFT_KEYS.map((l) => <SelectItem key={l.value || "none"} value={l.value || "none"}>{l.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={ex.intensity_pct} onChange={(e) => onChange("intensity_pct", e.target.value)} placeholder="%" className="h-9 text-sm text-center" />
        <Select value={ex.intensity_mode || "percent_tm"} onValueChange={(v) => onChange("intensity_mode", v)}>
          <SelectTrigger className="h-9 text-[10px]"><SelectValue /></SelectTrigger>
          <SelectContent>{INTENSITY_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={ex.rpe} onChange={(e) => onChange("rpe", e.target.value)} placeholder="RPE" className="h-9 text-sm text-center" />
        <Input value={ex.sets} onChange={(e) => onChange("sets", e.target.value)} placeholder="Sér." className="h-9 text-sm text-center" />
        <Input value={ex.reps} onChange={(e) => onChange("reps", e.target.value)} placeholder="Reps" className="h-9 text-sm text-center" />
        <Input value={ex.rest} onChange={(e) => onChange("rest", e.target.value)} placeholder="Repos" className="h-9 text-sm text-center" />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onRemove}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
      </div>
      {line && <p className="text-[11px] text-blue-600 mt-0.5">Cible : {line}{target?.type === "weight" && !line.includes("kg") ? ` · ${target.label}` : ""}</p>}
    </div>
  );
}

function RunningRow({ ex, onChange, onRemove, ctx, planDiscipline }) {
  const line = formatExercisePrescription(ex, planDiscipline, ctx);
  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 sm:grid-cols-[1fr,56px,72px,64px,56px,56px,48px,32px] gap-1.5">
        <Input value={ex.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Séance / bloc" className="h-9 text-sm col-span-2 sm:col-span-1" />
        <Input value={ex.sets} onChange={(e) => onChange("sets", e.target.value)} placeholder="×" className="h-9 text-sm text-center" title="Répétitions" />
        <Input value={ex.distance_km} onChange={(e) => onChange("distance_km", e.target.value)} placeholder="km" className="h-9 text-sm text-center" />
        <Input value={ex.target_pace} onChange={(e) => onChange("target_pace", e.target.value)} placeholder="4:30" className="h-9 text-sm text-center" title="Allure cible" />
        <Select value={ex.zone || "none"} onValueChange={(v) => onChange("zone", v === "none" ? "" : v)}>
          <SelectTrigger className="h-9 text-[10px]"><SelectValue placeholder="Zone" /></SelectTrigger>
          <SelectContent>{ZONE_OPTIONS.map((z) => <SelectItem key={z.value || "none"} value={z.value || "none"}>{z.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={ex.duration_min} onChange={(e) => onChange("duration_min", e.target.value)} placeholder="min" className="h-9 text-sm text-center" />
        <Input value={ex.rpe} onChange={(e) => onChange("rpe", e.target.value)} placeholder="RPE" className="h-9 text-sm text-center" />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onRemove}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
      </div>
      {line && <p className="text-[11px] text-emerald-700 mt-0.5">Prescription : {line}</p>}
    </div>
  );
}

function CyclingRow({ ex, onChange, onRemove, ctx, planDiscipline }) {
  const line = formatExercisePrescription(ex, planDiscipline, ctx);
  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 sm:grid-cols-[1fr,48px,56px,56px,64px,56px,48px,32px] gap-1.5">
        <Input value={ex.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Bloc vélo" className="h-9 text-sm col-span-2 sm:col-span-1" />
        <Input value={ex.sets} onChange={(e) => onChange("sets", e.target.value)} placeholder="×" className="h-9 text-sm text-center" />
        <Input value={ex.duration_min} onChange={(e) => onChange("duration_min", e.target.value)} placeholder="min" className="h-9 text-sm text-center" />
        <Input value={ex.target_pct_ftp} onChange={(e) => onChange("target_pct_ftp", e.target.value)} placeholder="%FTP" className="h-9 text-sm text-center" />
        <Input value={ex.target_watts} onChange={(e) => onChange("target_watts", e.target.value)} placeholder="W" className="h-9 text-sm text-center" />
        <Input value={ex.distance_km} onChange={(e) => onChange("distance_km", e.target.value)} placeholder="km" className="h-9 text-sm text-center" />
        <Input value={ex.rpe} onChange={(e) => onChange("rpe", e.target.value)} placeholder="RPE" className="h-9 text-sm text-center" />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onRemove}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
      </div>
      {line && <p className="text-[11px] text-sky-700 mt-0.5">Prescription : {line}</p>}
    </div>
  );
}

function SwimmingRow({ ex, onChange, onRemove, ctx, planDiscipline }) {
  const line = formatExercisePrescription(ex, planDiscipline, ctx);
  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 sm:grid-cols-[1fr,48px,72px,72px,56px,48px,32px] gap-1.5">
        <Input value={ex.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Série natation" className="h-9 text-sm col-span-2 sm:col-span-1" />
        <Input value={ex.sets} onChange={(e) => onChange("sets", e.target.value)} placeholder="×" className="h-9 text-sm text-center" />
        <Input value={ex.distance_m} onChange={(e) => onChange("distance_m", e.target.value)} placeholder="m" className="h-9 text-sm text-center" />
        <Input value={ex.target_pace_100m} onChange={(e) => onChange("target_pace_100m", e.target.value)} placeholder="1:35" className="h-9 text-sm text-center" title="/100m" />
        <Input value={ex.css_offset_sec} onChange={(e) => onChange("css_offset_sec", e.target.value)} placeholder="CSS+" className="h-9 text-sm text-center" />
        <Input value={ex.rpe} onChange={(e) => onChange("rpe", e.target.value)} placeholder="RPE" className="h-9 text-sm text-center" />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onRemove}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
      </div>
      {line && <p className="text-[11px] text-cyan-700 mt-0.5">Prescription : {line}</p>}
    </div>
  );
}

function GeneralRow({ ex, onChange, onRemove, planDiscipline, ctx }) {
  const line = formatExercisePrescription(ex, planDiscipline, ctx);
  return (
    <div className="mb-2">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr,72px,72px,1fr,32px] gap-1.5">
        <Input value={ex.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Activité" className="h-9 text-sm" />
        <Input value={ex.duration_min} onChange={(e) => onChange("duration_min", e.target.value)} placeholder="min" className="h-9 text-sm text-center" />
        <Input value={ex.rpe} onChange={(e) => onChange("rpe", e.target.value)} placeholder="RPE" className="h-9 text-sm text-center" />
        <Input value={ex.notes} onChange={(e) => onChange("notes", e.target.value)} placeholder="Notes" className="h-9 text-sm" />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onRemove}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
      </div>
      {line && <p className="text-[11px] text-amber-700 mt-0.5">{line}</p>}
    </div>
  );
}

export function ExerciseEditorHeader({ planDiscipline }) {
  if (planDiscipline === "hybrid") {
    return (
      <div className="hidden sm:grid grid-cols-[72px,1fr,56px,64px,64px,56px,48px,32px] gap-1 mb-1 text-[10px] font-medium text-slate-400 uppercase">
        <span>Type</span><span>Bloc</span><span>×</span><span>Cible</span><span>Durée/dist.</span><span>Zone</span><span>RPE</span><span />
      </div>
    );
  }
  const headers = {
    strength: ["Exercice", "Lift", "%", "Mode", "RPE", "Sér.", "Reps", "Repos", ""],
    running: ["Bloc", "×", "km", "Allure", "Zone", "min", "RPE", ""],
    cycling: ["Bloc", "×", "min", "%FTP", "W", "km", "RPE", ""],
    swimming: ["Série", "×", "m", "/100m", "CSS+", "RPE", ""],
    general: ["Activité", "min", "RPE", "Notes", ""],
  };
  const h = headers[planDiscipline] || headers.general;
  return (
    <div className={`hidden sm:grid gap-1 mb-1 text-[10px] font-medium text-slate-400 uppercase`} style={{ gridTemplateColumns: `repeat(${h.length}, minmax(0, 1fr))` }}>
      {h.map((label, i) => <span key={i}>{label}</span>)}
    </div>
  );
}

export default function TrainingExerciseEditor({
  exercise, planDiscipline, onUpdate, onRemove, liftMaxes = [], sportRefs = [],
}) {
  const ctx = { liftMaxes, sportRefs };
  const mod = resolveExerciseModality(exercise, planDiscipline);
  const onChange = (field, value) => onUpdate({ ...exercise, [field]: value });

  if (planDiscipline === "hybrid") {
    return (
      <div className="mb-2">
        <div className="grid grid-cols-[72px,1fr] gap-1.5 mb-1.5">
          <ModalitySelect value={mod} onChange={(v) => onUpdate({ ...exercise, modality: v })} />
        </div>
        <TrainingExerciseEditor
          exercise={exercise}
          planDiscipline={mod}
          onUpdate={onUpdate}
          onRemove={onRemove}
          liftMaxes={liftMaxes}
          sportRefs={sportRefs}
        />
      </div>
    );
  }

  if (mod === "running") return <RunningRow ex={exercise} onChange={onChange} onRemove={onRemove} ctx={ctx} planDiscipline={planDiscipline} />;
  if (mod === "cycling") return <CyclingRow ex={exercise} onChange={onChange} onRemove={onRemove} ctx={ctx} planDiscipline={planDiscipline} />;
  if (mod === "swimming") return <SwimmingRow ex={exercise} onChange={onChange} onRemove={onRemove} ctx={ctx} planDiscipline={planDiscipline} />;
  if (mod === "general") return <GeneralRow ex={exercise} onChange={onChange} onRemove={onRemove} planDiscipline={planDiscipline} ctx={ctx} />;
  return <StrengthRow ex={exercise} onChange={onChange} onRemove={onRemove} ctx={ctx} />;
}